import { Injectable } from '@angular/core';

const forge = require('node-forge');

@Injectable({
  providedIn: 'root',
})
export class CryptoService {
  constructor() {}

  encryptData(data, encryption_key) {
    //get encryption data key for form data encryption
    const dataKey = encryption_key;

    //setup node-forge ciper for encryption using random encryption key
    const cipher = forge.cipher.createCipher('AES-CBC', forge.util.decode64(dataKey.plain));
    cipher.start({ iv: forge.util.decode64(dataKey.plain) });
    cipher.update(forge.util.createBuffer(data));
    cipher.finish();

    //encode encrypted data as base64 to store in DB
    const result = forge.util.encode64(cipher.output.getBytes());

    //return encrypted key and data
    return {
      key: dataKey.encrypted,
      data: result,
    };
  }

  generateEncryptionKey(pk, sk, default_key = null) {
    let base64key;

    if (default_key) {
      base64key = default_key;
    } else {
      //generate a random key
      const randomKey = forge.random.getBytesSync(32);

      //convert random key to base 64
      base64key = forge.util.encode64(randomKey);
    }

    //encrypt key with node-forge
    //generate public key for encryption, format as PEM
    let publicKey;

    if (sk) {
      //if we have a security key, then use the public key here
      publicKey = forge.pki.publicKeyFromPem(sk.public_key);
    } else {
      publicKey = forge.pki.publicKeyFromPem(`-----BEGIN PUBLIC KEY-----
              ${pk}
              -----END PUBLIC KEY-----`);
    }

    //encrypt and return encoded key
    const result = forge.util.encode64(publicKey.encrypt(base64key, 'RSA-OAEP'));

    // return plain text data key and encrypted data key
    // plain text key should be used to encrypt and disgarded
    // encrypted data key should be stored with encrypted data for use in decrypting
    return {
      plain: base64key,
      encrypted: result,
    };
  }

  hashData(key) {
    let hashObject = forge.md.sha512.create();
    hashObject.update(key);
    let digest = forge.util.encode64(hashObject.digest().data);
    return digest;
  }

  async encryptFile(file, pk, encryption_key, security_key, sk): Promise<any> {
    //read contents of file as array buffer
    const fileData = await new Promise((resolve) => {
      const fileReader = new FileReader();

      fileReader.onloadend = (results) => {
        //return read results
        resolve(fileReader.result);
      };

      //initiate read
      fileReader.readAsBinaryString(file);
    });

    if (sk) {
      encryption_key = {
        plain: this.decryptEncryptionKey(encryption_key, security_key, sk),
        encrypted: encryption_key,
      };
    } else {
      encryption_key = this.generateEncryptionKey(pk, null, encryption_key);
    }

    //encrypt file data
    const encryptedFile = this.encryptData(fileData, encryption_key);

    //return encrypted file data
    return encryptedFile;
  }

  decryptWithPassword(data, password) {
    //setup node-forge ciper for encryption using password
    const decipher = forge.cipher.createDecipher('AES-CBC', password);
    decipher.start({ iv: password });
    decipher.update(forge.util.createBuffer(forge.util.decode64(data)));
    decipher.finish();

    //encode encrypted data as base64 to store in DB
    const results = decipher.output.toString();

    return results;
  }

  decryptFile(data, unencryptedDataKey) {
    //decrypt encrypted data using encryption key
    const decipher = forge.cipher.createDecipher('AES-CBC', forge.util.decode64(unencryptedDataKey));
    decipher.start({ iv: forge.util.decode64(unencryptedDataKey) });
    decipher.update(forge.util.createBuffer(forge.util.decode64(data)));
    decipher.finish();

    //output decrypted data
    const plaintext = this.touIntArray(decipher.output.getBytes());

    return plaintext;
  }

  touIntArray(str) {
    var view = new Uint8Array(str.length);
    for (var i = 0, j = str.length; i < j; i++) {
      view[i] = str.charCodeAt(i);
    }
    return view;
  }

  generateDataKeyPair(security_key) {
    //generate ppk
    const keyPair = forge.pki.rsa.generateKeyPair({ bits: 2048, e: 0x10001 });

    //return the ppk, but encrypt the private key with security key
    return {
      encrypted_key: forge.pki.encryptRsaPrivateKey(keyPair.privateKey, security_key),
      public_key: forge.pki.publicKeyToPem(keyPair.publicKey),
    };
  }

  encryptWithPassword(data, password) {
    //setup node-forge ciper for encryption using password
    const cipher = forge.cipher.createCipher('AES-CBC', password);
    cipher.start({ iv: password });
    cipher.update(forge.util.createBuffer(data));
    cipher.finish();

    //encode encrypted data as base64 to store in DB
    const results = forge.util.encode64(cipher.output.getBytes());

    return results;
  }

  decryptData(encrypted_data, security_key, sk) {
    //decrypt private key
    const privateKey = forge.pki.decryptRsaPrivateKey(security_key.encrypted_key, sk);

    //decrypted data key
    const unencryptedDataKey = privateKey.decrypt(forge.util.decode64(encrypted_data.key), 'RSA-OAEP');

    //decrypt encrypted data using decrypted data key
    const decipher = forge.cipher.createDecipher('AES-CBC', forge.util.decode64(unencryptedDataKey));
    decipher.start({ iv: forge.util.decode64(unencryptedDataKey) });
    decipher.update(forge.util.createBuffer(forge.util.decode64(encrypted_data.data)));
    decipher.finish();

    //output decrypted data
    const plaintext = decipher.output.toString();

    return plaintext;
  }

  decryptDataKey(encrypted_data, security_key, sk) {
    //decrypt private key
    const privateKey = forge.pki.decryptRsaPrivateKey(security_key.encrypted_key, sk);

    //decrypted data key
    return privateKey.decrypt(forge.util.decode64(encrypted_data), 'RSA-OAEP');
  }

  encryptWithPublicKey(data, pk) {
    const public_key = forge.pki.publicKeyFromPem(`-----BEGIN PUBLIC KEY-----
              ${pk}
              -----END PUBLIC KEY-----`);

    return forge.util.encode64(public_key.encrypt(data));
  }

  decryptEncryptionKey(encryption_key, security_key, sk) {
    //decrypt private key
    const privateKey = forge.pki.decryptRsaPrivateKey(security_key.encrypted_key, sk);

    //decrypted key
    return privateKey.decrypt(forge.util.decode64(encryption_key), 'RSA-OAEP');
  }

  async generateDataKeyPairAsync(security_key) {
    return await new Promise((resolve) => {
      if (typeof Worker !== 'undefined') {
        const worker = new Worker(new URL('./crypto.worker', import.meta.url), { type: 'module' });

        worker.onmessage = ({ data }) => {
          //return security key generated by worker process
          resolve(data);
        };

        worker.postMessage(security_key);
      } else {
        //browser does not support web-workers so just return a new data key, UI automation will be blocked
        resolve(this.generateDataKeyPair(security_key));
      }
    });
  }
}
